import React, { useEffect, useState, useRef } from 'react';
import { MdNotificationsActive } from 'react-icons/md';
import { FaCheckCircle, FaInfoCircle } from 'react-icons/fa';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, onSnapshot, updateDoc, doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useNavigate } from 'react-router-dom';

const NotificationBellWithDropdown = () => {
  const { currentUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [lastSession, setLastSession] = useState(0);
  const bellRef = useRef(null);
  const navigate = useNavigate();

  // Fetch lastSession from Firestore on mount
  useEffect(() => {
    const fetchLastSession = async () => {
      if (!currentUser) return;
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      if (userDoc.exists() && userDoc.data().lastSession) {
        setLastSession(userDoc.data().lastSession.seconds * 1000);
      } else {
        setLastSession(0);
      }
    };
    fetchLastSession();
  }, [currentUser]);

  // Real-time notifications
  useEffect(() => {
    if (!currentUser) return;
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', currentUser.uid)
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = [];
      snapshot.forEach(doc => notifs.push({ id: doc.id, ...doc.data() }));
      // Sort: unread first, then by createdAt desc
      notifs.sort((a, b) => {
        if (a.read === b.read) {
          return (b.createdAt?.seconds || 0) - (a.createdAt?.seconds || 0);
        }
        return a.read ? 1 : -1;
      });
      setNotifications(notifs);
    });
    return () => unsubscribe();
  }, [currentUser]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (bellRef.current && !bellRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [dropdownOpen]);

  // Compute unread notifications based on lastSession
  const notificationsWithSession = notifications.map(n => {
    const createdAtMs = n.createdAt?.seconds ? n.createdAt.seconds * 1000 : 0;
    return {
      ...n,
      isUnread: !n.read && createdAtMs > lastSession
    };
  });
  const unreadCount = notificationsWithSession.filter(n => n.isUnread).length;

  // Handle bell click: first click opens, second click marks all as read and updates lastSession
  const handleBellClick = async () => {
    if (dropdownOpen) {
      // Mark all as read (only those after lastSession)
      const unread = notificationsWithSession.filter(n => n.isUnread);
      await Promise.all(
        unread.map(n => updateDoc(doc(db, 'notifications', n.id), { read: true }))
      );
      // Update lastSession in Firestore
      if (currentUser) {
        await setDoc(doc(db, 'users', currentUser.uid), { lastSession: serverTimestamp() }, { merge: true });
        setLastSession(Date.now());
      }
    }
    setDropdownOpen(open => !open);
  };

  const handleNotificationClick = async (notif) => {
    if (!notif.read) {
      await updateDoc(doc(db, 'notifications', notif.id), { read: true });
    }
    setDropdownOpen(false);
    if (notif.link) {
      navigate(notif.link);
    }
  };

  const handleMarkAllRead = async () => {
    const unread = notificationsWithSession.filter(n => n.isUnread);
    await Promise.all(
      unread.map(n => updateDoc(doc(db, 'notifications', n.id), { read: true }))
    );
    if (currentUser) {
      await setDoc(doc(db, 'users', currentUser.uid), { lastSession: serverTimestamp() }, { merge: true });
      setLastSession(Date.now());
    }
  };

  // Color theme (adjust as needed)
  const theme = {
    bg: '#fff',
    border: '#e0e0e0',
    shadow: '0 4px 24px rgba(0,0,0,0.12)',
    accent: '#f44336',
    unreadBg: '#f9f9f9',
    text: '#222',
    muted: '#888',
    primary: '#1976d2'
  };

  return (
    <div className="notification-bell-dropdown" ref={bellRef} style={{ position: 'relative', display: 'inline-block' }}>
      <div style={{ cursor: 'pointer', position: 'relative' }} onClick={handleBellClick}>
        <MdNotificationsActive size={28} color={theme.primary} />
        {unreadCount > 0 && (
          <span
            style={{
              position: 'absolute',
              top: -4,
              right: -4,
              background: theme.accent,
              color: 'white',
              borderRadius: '50%',
              padding: '2px 7px',
              fontSize: '12px',
              fontWeight: 'bold',
              minWidth: '20px',
              textAlign: 'center',
              zIndex: 1,
              boxShadow: '0 1px 4px rgba(0,0,0,0.15)'
            }}
          >
            {unreadCount}
          </span>
        )}
      </div>
      {dropdownOpen && (
        <div
          className="notification-dropdown-panel"
          style={{
            position: 'absolute',
            right: 0,
            top: 36,
            width: 340,
            maxHeight: 420,
            background: theme.bg,
            border: `1px solid ${theme.border}`,
            borderRadius: 12,
            boxShadow: theme.shadow,
            zIndex: 1000,
            overflowY: 'auto',
            minWidth: 280
          }}
        >
          <div style={{ padding: '16px 16px 8px 16px', borderBottom: `1px solid ${theme.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600, color: theme.text, fontSize: 16 }}>Notifications</span>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                style={{
                  background: theme.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 10px',
                  fontSize: 13,
                  cursor: 'pointer'
                }}
              >
                Mark all as read
              </button>
            )}
          </div>
          <div>
            {notificationsWithSession.length === 0 ? (
              <div style={{ padding: 24, color: theme.muted, textAlign: 'center' }}>
                <FaInfoCircle size={32} style={{ marginBottom: 8 }} />
                <div>No notifications yet.</div>
              </div>
            ) : (
              notificationsWithSession.map((notif) => (
                <div
                  key={notif.id}
                  className={`notification-item${notif.link ? ' clickable' : ''}`}
                  onClick={() => notif.link && handleNotificationClick(notif)}
                  style={{
                    background: notif.isUnread ? theme.unreadBg : theme.bg,
                    borderBottom: `1px solid ${theme.border}`,
                    padding: '14px 16px',
                    cursor: notif.link ? 'pointer' : 'default',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 12,
                    transition: 'background 0.15s',
                  }}
                >
                  <div style={{ marginTop: 2 }}>
                    <FaCheckCircle color={notif.isUnread ? theme.accent : theme.primary} size={18} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ color: theme.text, fontWeight: notif.isUnread ? 600 : 400, fontSize: 15 }}>
                      {notif.title || 'Notification'}
                    </div>
                    <div style={{ color: theme.muted, fontSize: 13, marginTop: 2 }}>
                      {notif.message}
                    </div>
                    <div style={{ color: theme.muted, fontSize: 12, marginTop: 4 }}>
                      {notif.createdAt?.seconds
                        ? new Date(notif.createdAt.seconds * 1000).toLocaleString()
                        : ''}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBellWithDropdown; 